import React, { useEffect, useState, Component } from 'react';
import { X, CheckCircle2, Mail } from 'lucide-react';
import confetti from 'canvas-confetti';
import { Button } from './ui/Button';
const SUPPORT_EMAIL = 'support@zanoplatform.com';
// Web3Forms delivers submissions straight to your inbox — no email client.
// Get a free access key at https://web3forms.com and set its destination to
// support@zanoplatform.com, then paste the key below.
const WEB3FORMS_ACCESS_KEY = 'daa0e880-90c5-42b7-911c-ac1a7e0e4820';
export const MarketplaceModal = ({
  isOpen,
  onClose
}: {
  isOpen: boolean;
  onClose: () => void;
}) => {
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Reset back to the form whenever the modal is fully closed.
  useEffect(() => {
    if (!isOpen) {
      const t = window.setTimeout(() => {
        setSubmitted(false);
        setError(null);
        setSubmitting(false);
      }, 250);
      return () => window.clearTimeout(t);
    }
  }, [isOpen]);
  if (!isOpen) return null;
  const inputClass = 'w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-zano-pink focus:border-zano-pink outline-none text-sm';
  const labelClass = 'block text-sm font-medium text-gray-700 mb-1';
  const rainConfetti = () => {
    const duration = 2500;
    const end = Date.now() + duration;
    const colors = ['#EC4899', '#F9A8D4', '#C4B5FD', '#38BDF8', '#FBCFE8'];
    confetti({
      particleCount: 120,
      spread: 90,
      origin: {
        y: 0.3
      },
      colors
    });
    const frame = () => {
      confetti({
        particleCount: 4,
        angle: 90,
        spread: 70,
        startVelocity: 35,
        origin: {
          x: Math.random(),
          y: -0.1
        },
        gravity: 0.8,
        colors
      });
      if (Date.now() < end) {
        requestAnimationFrame(frame);
      }
    };
    frame();
  };
  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError(null);
    const data = new FormData(e.currentTarget);
    const get = (k: string) => data.get(k) as string || '—';
    // Send the submission straight to support@zanoplatform.com via Web3Forms.
    const message = ['ABOUT THE USER', `Full name: ${get('fullName')}`, `Work email: ${get('email')}`, `LinkedIn profile URL: ${get('linkedin')}`, `Job title: ${get('jobTitle')}`, `Country: ${get('country')}`, '', 'COMPANY DETAILS', `Company name: ${get('companyName')}`, `Company website: ${get('website')}`, `Business type: ${get('businessType')}`, '', 'CAMPAIGN DETAILS', `Influencer campaigns per month: ${get('campaignsPerMonth')}`, `Estimated monthly budget: ${get('monthlyBudget')}`, `Type of talent looking for: ${get('talentType')}`, '', 'OTHER INFO', `How did you hear about Zano: ${get('howDidYouHear')}`, `Referred by a Zano user?: ${get('referredBy')}`, `Anything else?: ${get('notes')}`].join('\n');
    const payload = {
      access_key: WEB3FORMS_ACCESS_KEY,
      subject: 'New Marketplace Access Request',
      from_name: 'Zano — Request Marketplace Access form',
      replyto: get('email'),
      message
    };
    try {
      setSubmitting(true);
      const res = await fetch('https://api.web3forms.com/submit', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json'
        },
        body: JSON.stringify(payload)
      });
      const result = await res.json();
      if (!res.ok || !result.success) {
        throw new Error(result.message || 'Submission failed');
      }
      rainConfetti();
      setSubmitted(true);
    } catch (err) {
      setError('Something went wrong sending your request. Please try again or email us directly.');
    } finally {
      setSubmitting(false);
    }
  };
  return <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-gray-900/50 backdrop-blur-sm" onClick={onClose} data-id="element-1055" data-mp="2o9ygajmav1o9">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-3xl overflow-hidden relative max-h-[90vh] flex flex-col" onClick={e => e.stopPropagation()} data-id="element-1056" data-mp="2jc6r3xd1htnr">
        <button onClick={onClose} aria-label="Close" className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 z-10" data-id="element-1057" data-mp="2ta2yqf6rq6h4">
          <X size={20} data-id="element-1058" data-mp="327wthlovws26" />
        </button>

        {submitted ? <div className="p-8 sm:p-10 text-center" data-id="element-1059" data-mp="3gxb1oqgq53fz">
            <div className="mx-auto mb-5 flex items-center justify-center w-16 h-16 rounded-full bg-emerald-50" data-id="element-1060" data-mp="3gwsytazk3qz3">
              <CheckCircle2 size={32} className="text-emerald-500" data-id="element-1061" data-mp="3c0kme5cqubvm" />
            </div>
            <h3 className="text-2xl font-bold text-gray-900 mb-3" data-id="element-1062" data-mp="3pyioocdsihur">
              Request received!
            </h3>
            <p className="text-gray-600 text-sm leading-relaxed mb-6" data-id="element-1063" data-mp="3luugpsl8xk8z">
              Thank you, we received your request. Our team will get back to you
              within 48 hours. If you are approved, you will receive an
              invitation to create an account as a marketplace user and
              subscribe to a free trial on the marketplace plan. Any questions,
              email us at{' '}
              <a href={`mailto:${SUPPORT_EMAIL}`} className="text-zano-pink hover:underline" data-id="element-1064" data-mp="3lugipnr6dos1">
                {SUPPORT_EMAIL}
              </a>
              .
            </p>
            <Button variant="primary" className="w-full sm:w-auto justify-center px-8" onClick={onClose} data-id="element-1065" data-mp="3cubl25bsa4r2">
              Done
            </Button>
          </div> : <div className="p-6 sm:p-8 overflow-y-auto" data-id="element-1066" data-mp="32wx484ly7dy8">
            <h3 className="text-2xl font-bold text-gray-900 mb-2" data-id="element-1067" data-mp="37ufimaiqildm">
              Request Marketplace Access
            </h3>
            <p className="text-gray-600 mb-8 text-sm leading-relaxed" data-id="element-1068" data-mp="1deqm4r6m4mnu">
              Get access to talent agency rosters, represented influencer
              profiles, marketplace benchmarks, average pricing insights, and
              the Zano Dashboard. Complete the form below and our team will
              review your request.
            </p>

            <form className="space-y-6" onSubmit={handleSubmit} data-id="element-1069" data-mp="zl8zgximqttl">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5" data-id="element-1070" data-mp="1wc1k88gida1p">
                <div data-id="element-1071" data-mp="1sb7quzkar31f">
                  <label className={labelClass} data-id="element-1072" data-mp="1sd2ggr6ijjbu">Full name</label>
                  <input name="fullName" type="text" required className={inputClass} data-id="element-1073" data-mp="1ncei1kfyg07l" />
                </div>
                <div data-id="element-1074" data-mp="by0wwncl5yv9">
                  <label className={labelClass} data-id="element-1075" data-mp="byam48pt41zw">Work email</label>
                  <input name="email" type="email" required className={inputClass} data-id="element-1076" data-mp="6yo7pvy8sfpi" />
                </div>
                <div data-id="element-1077" data-mp="1t18h3vcj5q3">
                  <label className={labelClass} data-id="element-1078" data-mp="1um3eulkoqoh">LinkedIn profile URL</label>
                  <input name="linkedin" type="url" required className={inputClass} data-id="element-1079" data-mp="6s44jytcm1lx" />
                </div>
                <div data-id="element-1080" data-mp="flh7zup1glm9">
                  <label className={labelClass} data-id="element-1081" data-mp="fk5zxiok8rpc">Job title</label>
                  <input name="jobTitle" type="text" required className={inputClass} data-id="element-1082" data-mp="kjshu5fc2w4h" />
                </div>
                <div data-id="element-1083" data-mp="uiroxjkgajpd">
                  <label className={labelClass} data-id="element-1084" data-mp="ugx3831cunk8">Country</label>
                  <input name="country" type="text" required className={inputClass} data-id="element-1085" data-mp="2rarwgtxx824l" />
                </div>
                <div data-id="element-1086" data-mp="1217ou42m5esl">
                  <label className={labelClass} data-id="element-1087" data-mp="120pl6mdmfyan">Company name</label>
                  <input name="companyName" type="text" required className={inputClass} data-id="element-1088" data-mp="x2xrpnxbw44c" />
                </div>
                <div data-id="element-1089" data-mp="16rxulieybjnt">
                  <label className={labelClass} data-id="element-1090" data-mp="16tipj956h4m7">Company website</label>
                  <input name="website" type="url" required className={inputClass} data-id="element-1091" data-mp="j2n4cuv897nx" />
                </div>
                <div data-id="element-1092" data-mp="s431i0x17da7">
                  <label className={labelClass} data-id="element-1093" data-mp="s4mm5pe2gfrx">
                    What kind of business are you?
                  </label>
                  <select name="businessType" required className={inputClass} data-id="element-1094" data-mp="o0okb6xcwqv6">
                    <option value="" disabled selected data-id="element-1095" data-mp="23hf5qcwpdxww">
                      Select an option
                    </option>
                    <option data-id="element-1096" data-mp="1zj7rboppp8ov">Brand</option>
                    <option data-id="element-1097" data-mp="2704jx847532g">Marketing Agency</option>
                    <option data-id="element-1098" data-mp="26zcru79t5ngx">Talent Agency</option>
                  </select>
                </div>
                <div data-id="element-1099" data-mp="2c122vmx16150">
                  <label className={labelClass} data-id="element-1100" data-mp="1x2hrditz5d00">
                    How many influencer campaigns do you run each month?
                  </label>
                  <input name="campaignsPerMonth" type="text" required className={inputClass} data-id="element-1101" data-mp="222mcjm5tuvwk" />
                </div>
                <div data-id="element-1102" data-mp="2upp6s23h0kh0">
                  <label className={labelClass} data-id="element-1103" data-mp="2usbf43y6r40a">
                    Estimated monthly budget for influencer marketing
                  </label>
                  <input name="monthlyBudget" type="text" required className={inputClass} data-id="element-1104" data-mp="2kznfl1ftmlxv" />
                </div>
                <div className="sm:col-span-2" data-id="element-1105" data-mp="3dhrntek0y2rs">
                  <label className={labelClass} data-id="element-1106" data-mp="3dikrg6nosc5p">
                    What kind of talent are you looking to work with?
                  </label>
                  <input name="talentType" type="text" placeholder="e.g. influencers, celebrities, public figures, athletes, B2B LinkedIn" required className={inputClass} data-id="element-1107" data-mp="1ov75kq5ocdpk" />
                </div>
                <div className="sm:col-span-2" data-id="element-1108" data-mp="115uq58jr79et">
                  <label className={labelClass} data-id="element-1109" data-mp="15xcpxta1j3y7">
                    How did you hear about Zano?
                  </label>
                  <select name="howDidYouHear" required className={inputClass} data-id="element-1110" data-mp="s6r3juldobvk">
                    <option value="" disabled selected data-id="element-1111" data-mp="x4sybcs61zmv">
                      Select an option
                    </option>
                    <option data-id="element-1112" data-mp="i7iglloxjxip">LinkedIn</option>
                    <option data-id="element-1113" data-mp="i9v3md9joy2m">YouTube</option>
                    <option data-id="element-1114" data-mp="n7ekzm1jhqys">Google</option>
                    <option data-id="element-1115" data-mp="n63ehegpm53z">Word of mouth</option>
                    <option data-id="element-1116" data-mp="3kjpxsxwd751">WIIM</option>
                    <option data-id="element-1117" data-mp="3llaco0sz5zf">ChatGPT</option>
                    <option data-id="element-1118" data-mp="3vp5ezu3rx4xi">Referral from existing user</option>
                    <option data-id="element-1119" data-mp="3vqovnltcdhzt">Influencer Marketing Hub</option>
                    <option data-id="element-1120" data-mp="djiwn5z3qspe">Cannes Lions 2026</option>
                    <option data-id="element-1121" data-mp="dgwhol9vtax5">Product Hunt</option>
                    <option data-id="element-1122" data-mp="8j4pdq5brmsf">Email outreach</option>
                    <option data-id="element-1123" data-mp="8mircxa2ttzc">The Creator Network</option>
                    <option data-id="element-1124" data-mp="mimiuitkfo84">Other</option>
                  </select>
                </div>
                <div className="sm:col-span-2" data-id="element-1125" data-mp="ihara7r4ssxa">
                  <label className={labelClass} data-id="element-1126" data-mp="igj5fonc69k7">
                    Were you referred by a Zano user? If yes, who?
                  </label>
                  <input name="referredBy" type="text" className={inputClass} data-id="element-1127" data-mp="rae1862kinn6" />
                </div>
                <div className="sm:col-span-2" data-id="element-1128" data-mp="16754wrj9ylil">
                  <label className={labelClass} data-id="element-1129" data-mp="165u0qqlx3bqy">
                    Anything else you would like us to know?
                  </label>
                  <textarea name="notes" rows={3} className={inputClass} data-id="element-1130" data-mp="18cjsoeien1j2" />
                </div>
              </div>

              <div className="flex items-start gap-3 pt-2" data-id="element-1131" data-mp="1dc672r9yyntg">
                <input type="checkbox" id="marketplace-tos" name="tos" required className="mt-1 shrink-0 rounded border-gray-300 text-zano-pink focus:ring-zano-pink" data-id="element-1132" data-mp="1ia7pd9zkmj3v" />
                <label htmlFor="marketplace-tos" className="text-[11px] text-gray-500 leading-relaxed" data-id="element-1133" data-mp="1rzr6aa3q42w3">
                  By checking this box, I acknowledge and agree to the Terms of
                  Service and Privacy Policy by Zano Platform. I acknowledge
                  that Zano Platform may collect, process, and store my personal
                  data provided in this form for the purpose of evaluating my
                  request and providing access to the platform. I understand
                  that my data will be handled in accordance with the Zano
                  Privacy Policy and GDPR regulations.
                </label>
              </div>

              {error && <p className="text-sm text-red-600" role="alert" data-id="element-1134" data-mp="3qh1gti448lxn">
                  {error}
                </p>}
              <Button variant="primary" className="w-full justify-center py-3 disabled:opacity-60 disabled:cursor-not-allowed" type="submit" disabled={submitting} data-id="element-1135" data-mp="3lk2qlankoz4q">
                {submitting ? 'Sending…' : 'Submit request'}
              </Button>
            </form>
          </div>}
      </div>
    </div>;
};